<template>
  <v-navigation-drawer
    :value="value"
    @input="onDrawerInput"
    fixed
    right
    temporary
    width="680"
    class="artwork-photo-drawer"
  >
    <v-toolbar flat color="grey lighten-3">
      <v-toolbar-title>Edit artwork image</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="requestClose"><v-icon>close</v-icon></v-btn>
    </v-toolbar>

    <v-progress-linear v-if="loading" indeterminate color="black"></v-progress-linear>

    <div v-if="photo" class="pa-4">
      <v-alert v-if="error" :value="true" type="error" class="mb-3">{{ error }}</v-alert>
      <v-alert v-if="savedMessage" :value="true" type="success" class="mb-3">{{ savedMessage }}</v-alert>

      <div class="photo-summary mb-4">
        <div class="photo-preview grey lighten-3">
          <img v-if="previewUrl && !imageFailed" :src="previewUrl" :alt="photo.fname" @error="imageFailed = true" />
          <div v-else class="photo-preview-placeholder">
            <v-icon>broken_image</v-icon>
            <span>Image not ready</span>
          </div>
        </div>
        <div class="photo-summary-text">
          <div class="title">{{ photo.fname || `Image ${photo.id}` }}</div>
          <div v-if="photo.width || photo.height" class="caption grey--text">
            {{ photo.width || '?' }} × {{ photo.height || '?' }} pixels
          </div>
          <div class="caption grey--text">Artwork image #{{ photo.id }}</div>
          <artwork-media-status :media="photo" />
          <v-chip v-if="photo.netx_source" small label outline class="ml-0 mt-2">Imported from NetX</v-chip>
          <v-chip v-else small label outline class="ml-0 mt-2">Uploaded to CMS</v-chip>
        </div>
      </div>

      <v-alert
        v-if="photo.netx_source && liveSourceDiffers"
        :value="true"
        type="warning"
        outline
        class="mb-3"
      >
        The CMS image information differs from the latest NetX data. Local CMS values are kept unless you choose to replace them.
      </v-alert>

      <v-textarea
        v-model="form.credits"
        label="Credits"
        rows="3"
        auto-grow
        :disabled="formDisabled"
      ></v-textarea>
      <div v-if="sourceFieldDiffers('credits')" class="source-difference mb-3">
        <div class="source-difference-label">Latest NetX credits</div>
        <div class="source-difference-value">{{ displaySourceValue('credits') }}</div>
      </div>

      <v-textarea
        v-model="form.description_credit"
        label="Description (Credit)"
        rows="3"
        auto-grow
        :disabled="formDisabled"
      ></v-textarea>
      <div v-if="sourceFieldDiffers('description_credit')" class="source-difference mb-3">
        <div class="source-difference-label">Latest NetX Description (Credit)</div>
        <div class="source-difference-value">{{ displaySourceValue('description_credit') }}</div>
      </div>

      <div v-if="photo.netx_source" class="d-flex align-center mb-4">
        <v-btn
          small
          flat
          color="deep-orange darken-2"
          class="ml-0"
          :disabled="formDisabled || !liveSourceDiffers"
          @click="useLatestNetxValues"
        >
          <v-icon small left>sync</v-icon>Use latest NetX values
        </v-btn>
        <span class="caption grey--text">Changes are applied only after Save.</span>
      </div>

      <v-divider class="mb-3"></v-divider>
      <div class="subheading font-weight-bold mb-2">Image settings</div>
      <div class="photo-settings">
        <v-switch v-model="form.cms_top" label="CMS top" color="black" :disabled="formDisabled"></v-switch>
        <v-switch
          v-model="form.public_site"
          label="Public website"
          color="green"
          :disabled="formDisabled"
          @change="onPublicSiteChange"
        ></v-switch>
        <v-switch
          v-model="form.public_top"
          label="Public top"
          color="green"
          :disabled="formDisabled"
          @change="onPublicTopChange"
        ></v-switch>
        <v-switch v-model="form.ftb_favorite" label="FTB favorite" color="black" :disabled="formDisabled"></v-switch>
        <v-switch v-model="form.ftb_approved" label="FTB approved" color="black" :disabled="formDisabled"></v-switch>
        <v-switch v-model="form.publication" label="Publication" color="black" :disabled="formDisabled"></v-switch>
      </div>

      <div class="caption grey--text mb-4">
        NetX remains read-only. These settings and text belong to the imported or uploaded CMS artwork image.
      </div>

      <div class="d-flex justify-end">
        <v-btn flat @click="requestClose">Cancel</v-btn>
        <v-btn
          dark
          color="black"
          :loading="saving"
          :disabled="formDisabled"
          @click="save"
        >Save image</v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import axios from '@/utils/apiClient'
import {
  cmsImageUrl,
  normalizeMediaUrl,
  publicImageUrl
} from '@/utils/artworkMedia'
import ArtworkMediaStatus from './ArtworkMediaStatus'

const emptyForm = () => ({
  credits: '',
  description_credit: '',
  cms_top: false,
  public_site: false,
  public_top: false,
  ftb_favorite: false,
  ftb_approved: false,
  publication: false
})

export default {
  name: 'ArtworkPhotoDrawer',
  components: { ArtworkMediaStatus },
  props: {
    value: { type: Boolean, default: false },
    artwork: { type: Object, default: null },
    photoId: { type: [Number, String], default: null }
  },
  data: () => ({
    photo: null,
    form: emptyForm(),
    loading: false,
    saving: false,
    error: '',
    savedMessage: '',
    snapshot: '',
    imageFailed: false,
    loadScheduled: false
  }),
  computed: {
    canEdit () {
      const roles = (this.c_user && this.c_user.roles) || []
      return !!(this.c_user && (this.c_user.admin === true || Number(this.c_user.admin) === 1)) ||
        roles.some(role => ['admin', 'superadmin', 'root'].includes(String(role.shortcut || role.role || '').toLowerCase()))
    },
    formDisabled () {
      return this.loading || this.saving || !this.canEdit
    },
    isDirty () {
      return !!this.snapshot && this.snapshot !== this.serializeForm()
    },
    previewUrl () {
      return normalizeMediaUrl(
        cmsImageUrl(this.photo) || publicImageUrl(this.photo, 1280),
        this.$globals.apiserver
      )
    },
    liveSourceDiffers () {
      return this.sourceFieldDiffers('credits') || this.sourceFieldDiffers('description_credit')
    }
  },
  watch: {
    value (open) {
      if (open) this.scheduleLoad()
    },
    photoId (next, previous) {
      if (this.value && Number(next) !== Number(previous)) this.scheduleLoad()
    }
  },
  methods: {
    scheduleLoad () {
      if (this.loadScheduled) return
      this.loadScheduled = true
      this.$nextTick(() => {
        this.loadScheduled = false
        if (this.value) this.load()
      })
    },
    serializeForm () {
      return JSON.stringify(this.payloadAttributes())
    },
    payloadAttributes () {
      return {
        credits: this.form.credits || '',
        description_credit: this.form.description_credit || '',
        cms_top: !!this.form.cms_top,
        public_site: !!this.form.public_site,
        public_top: !!this.form.public_top,
        ftb_favorite: !!this.form.ftb_favorite,
        ftb_approved: !!this.form.ftb_approved,
        publication: !!this.form.publication
      }
    },
    async load () {
      if (!this.photoId) return
      this.loading = true
      this.error = ''
      this.savedMessage = ''
      this.snapshot = ''
      this.imageFailed = false
      try {
        const response = await axios.get(`${this.$globals.apiserver}/artwork_photos/${this.photoId}.json`)
        this.setPhoto(response.data)
      } catch (error) {
        this.photo = null
        this.error = this.errorMessage(error, 'Artwork image details could not be loaded.')
      } finally {
        this.loading = false
      }
    },
    setPhoto (photo) {
      this.photo = photo
      this.form = {
        credits: photo.credits || '',
        description_credit: photo.description_credit || '',
        cms_top: !!photo.cms_top,
        public_site: !!photo.public_site,
        public_top: !!photo.public_top,
        ftb_favorite: !!photo.ftb_favorite,
        ftb_approved: !!photo.ftb_approved,
        publication: !!photo.publication
      }
      this.$nextTick(() => { this.snapshot = this.serializeForm() })
    },
    sourceFieldDiffers (field) {
      if (!this.photo || !this.photo.netx_source) return false
      return this.normalizedValue(this.form[field]) !== this.normalizedValue(this.photo.netx_source[field])
    },
    normalizedValue (value) {
      return String(value || '').replace(/\r\n/g, '\n').trim()
    },
    displaySourceValue (field) {
      const value = this.photo && this.photo.netx_source && this.photo.netx_source[field]
      return value || 'Empty'
    },
    useLatestNetxValues () {
      if (!this.photo || !this.photo.netx_source) return
      this.form.credits = this.photo.netx_source.credits || ''
      this.form.description_credit = this.photo.netx_source.description_credit || ''
    },
    onPublicSiteChange (selected) {
      if (!selected) this.form.public_top = false
    },
    onPublicTopChange (selected) {
      if (selected) this.form.public_site = true
    },
    async save () {
      if (!this.photo || !this.canEdit || this.saving) return
      this.saving = true
      this.error = ''
      this.savedMessage = ''
      try {
        const response = await axios.patch(
          `${this.$globals.apiserver}/artwork_photos/${this.photo.id}.json`,
          { artwork_photo: this.payloadAttributes() }
        )
        this.setPhoto(response.data)
        const queued = !!response.data.public_processing_queued
        this.$emit('saved', {
          artworkId: response.data.artwork_id,
          photo: response.data,
          publicProcessingQueued: queued
        })
        if (queued) {
          this.savedMessage = 'Image details saved. Public image versions are processing.'
        } else {
          this.$emit('input', false)
        }
      } catch (error) {
        this.error = this.errorMessage(error, 'Artwork image details could not be saved.')
      } finally {
        this.saving = false
      }
    },
    onDrawerInput (open) {
      if (!open) this.requestClose()
    },
    requestClose () {
      if (this.isDirty && !window.confirm('Discard unsaved artwork image changes?')) return
      this.$emit('input', false)
    },
    errorMessage (error, fallback) {
      const data = error && error.response && error.response.data
      return (data && (data.error || (data.errors && data.errors.join(', ')))) || fallback
    }
  }
}
</script>

<style scoped>
.artwork-photo-drawer { z-index: 20; }
.photo-summary { align-items: flex-start; display: flex; gap: 14px; }
.photo-preview { align-items: center; display: flex; flex: 0 0 190px; height: 130px; justify-content: center; overflow: hidden; }
.photo-preview img { height: 100%; object-fit: contain; width: 100%; }
.photo-preview-placeholder { align-items: center; color: #777; display: flex; flex-direction: column; font-size: 12px; gap: 5px; }
.photo-summary-text { min-width: 0; padding-top: 3px; }
.photo-summary-text .title { overflow-wrap: anywhere; }
.source-difference { background: #fff7e8; border-left: 3px solid #ef8c20; padding: 8px 10px; }
.source-difference-label { color: #8a4b00; font-size: 11px; font-weight: 700; text-transform: uppercase; }
.source-difference-value { color: #4c3926; font-size: 12px; margin-top: 3px; white-space: pre-wrap; }
.photo-settings { display: grid; grid-template-columns: 1fr 1fr; }
.photo-settings .v-input { margin-top: 0; }
@media (max-width: 700px) {
  .photo-summary { flex-direction: column; }
  .photo-preview { flex-basis: auto; width: 100%; }
  .photo-settings { grid-template-columns: 1fr; }
}
</style>



// WEBPACK FOOTER //
// src/components/artworks/ArtworkPhotoDrawer.vue